/**
 * Filterzustand der Ratentabelle auf der Liquiditaetsplanung. Drei Quellen wirken zusammen, alle
 * UND-verknuepft: eine Kachel, eine angeklickte Woche im Diagramm und die Spaltenfilter.
 *
 * Reine Funktionen, damit Chips ("x" je Filter) und Tabelle dieselbe Quelle lesen.
 */
import {
  passtZurKachel,
  passtZurWoche,
  transportartLabel,
  wochenLabel,
  type KachelFilter,
  type LiquiditaetsRate,
  type LiquiditaetsWoche,
  type WochenAuswahl,
} from "../../lib/liquiditaet.ts";
import type { Bereichsfilter } from "../../lib/orderList.ts";
import { fmtDate } from "../../lib/format.ts";

export interface RatenFilter {
  kachel: KachelFilter | null;
  woche: WochenAuswahl | null;
  bestellung: string;
  lieferant: string[];
  status: string[];
  stock: string[];
  transport: string[];
  faellig: Bereichsfilter;
  betrag: Bereichsfilter;
}

export const LEERER_FILTER: RatenFilter = {
  kachel: null,
  woche: null,
  bestellung: "",
  lieferant: [],
  status: [],
  stock: [],
  transport: [],
  faellig: {},
  betrag: {},
};

export const KACHEL_LABEL: Readonly<Record<KachelFilter, string>> = {
  fix: "Fällig fix",
  geplant: "Geplant",
  ohneDatum: "Ohne Datum",
  stock: "Stock Orders",
  ueberfaellig: "Überfällig",
};

export const STATUS_LABEL: Readonly<Record<LiquiditaetsRate["klasse"], string>> = {
  fix: "Fix",
  geplant: "Geplant",
  offen: "Offen",
};

export function stockWert(rate: LiquiditaetsRate): string {
  return rate.stock ? "ja" : "nein";
}

const bereichAktiv = (b: Bereichsfilter): boolean => Boolean(b.von || b.bis || b.ohneWert);

export function filterAktiv(f: RatenFilter): boolean {
  return Boolean(
    f.kachel || f.woche || f.bestellung.trim() || f.lieferant.length || f.status.length ||
      f.stock.length || f.transport.length || bereichAktiv(f.faellig) || bereichAktiv(f.betrag),
  );
}

export function wendeFilterAn(
  raten: readonly LiquiditaetsRate[],
  f: RatenFilter,
  wochen: readonly LiquiditaetsWoche[],
  heute: string,
): LiquiditaetsRate[] {
  const suche = f.bestellung.trim().toLowerCase();
  const von = Number(f.betrag.von);
  const bis = Number(f.betrag.bis);
  return raten.filter((r) => {
    if (f.kachel && !passtZurKachel(r, f.kachel)) return false;
    if (f.woche && !passtZurWoche(r, f.woche, wochen, heute)) return false;
    if (suche && !r.orderNo.toLowerCase().includes(suche)) return false;
    if (f.lieferant.length && !f.lieferant.includes(r.lieferant)) return false;
    if (f.status.length && !f.status.includes(r.klasse)) return false;
    if (f.stock.length && !f.stock.includes(stockWert(r))) return false;
    if (f.transport.length && !f.transport.includes(r.transportMode)) return false;
    if (f.faellig.ohneWert) {
      if (r.datum !== null) return false;
    } else {
      if (f.faellig.von && (r.datum === null || r.datum < f.faellig.von)) return false;
      if (f.faellig.bis && (r.datum === null || r.datum > f.faellig.bis)) return false;
    }
    if (f.betrag.von && Number.isFinite(von) && r.usd < von) return false;
    if (f.betrag.bis && Number.isFinite(bis) && r.usd > bis) return false;
    return true;
  });
}

export interface FilterChip {
  /** Welcher Filter - der Chip loescht genau diesen. */
  art: keyof RatenFilter;
  text: string;
}

function bereichText(b: Bereichsfilter, formatiere: (s: string) => string): string {
  if (b.ohneWert) return "ohne Wert";
  if (b.von && b.bis) return `${formatiere(b.von)} – ${formatiere(b.bis)}`;
  return b.von ? `ab ${formatiere(b.von)}` : `bis ${formatiere(b.bis ?? "")}`;
}

export function filterChips(f: RatenFilter, wochen: readonly LiquiditaetsWoche[]): FilterChip[] {
  const chips: FilterChip[] = [];
  if (f.kachel) chips.push({ art: "kachel", text: `Kachel: ${KACHEL_LABEL[f.kachel]}` });
  if (f.woche) {
    const woche = wochen.find((w) => w.key === f.woche?.key);
    const name = woche ? wochenLabel(woche) : f.woche.key;
    chips.push({
      art: "woche",
      text: f.woche.modus === "kumuliert" ? `Bis ${name}` : name,
    });
  }
  if (f.bestellung.trim()) chips.push({ art: "bestellung", text: `Bestellung: „${f.bestellung.trim()}“` });
  if (f.lieferant.length) chips.push({ art: "lieferant", text: `Lieferant: ${f.lieferant.join(", ")}` });
  if (f.status.length) {
    chips.push({ art: "status", text: `Status: ${f.status.map((s) => STATUS_LABEL[s as LiquiditaetsRate["klasse"]] ?? s).join(", ")}` });
  }
  if (f.stock.length) chips.push({ art: "stock", text: `Stock Order: ${f.stock.map((s) => (s === "ja" ? "Ja" : "Nein")).join(", ")}` });
  if (f.transport.length) {
    chips.push({ art: "transport", text: `Transportart: ${f.transport.map((t) => transportartLabel(t as "see" | "air" | "road")).join(", ")}` });
  }
  if (bereichAktiv(f.faellig)) chips.push({ art: "faellig", text: `Fällig: ${bereichText(f.faellig, fmtDate)}` });
  if (bereichAktiv(f.betrag)) chips.push({ art: "betrag", text: `Betrag: ${bereichText(f.betrag, (s) => `$ ${s}`)}` });
  return chips;
}
